import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Book, Menu, X, ArrowRight } from 'lucide-react';
import { useOptionalAuth } from '../context/AuthContext';
import { useTenant, useMarketingTheme } from '../context/TenantContext';

/** Theme-aware public navbar. Three visual variants matching the marketing themes. */
export const PublicNavbar: React.FC = () => {
  const location = useLocation();
  const auth = useOptionalAuth();
  const { tenant } = useTenant();
  const theme = useMarketingTheme();
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const tenantName = tenant?.name ?? 'Kula Learn';
  const initial = tenantName.charAt(0).toUpperCase();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setScrolled(prev => {
          const next = window.scrollY > 10;
          return prev === next ? prev : next;
        });
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const isActive = (path: string) => location.pathname === path;
  const browseHref = isAuthenticated ? '/browse' : '/courses';
  const browseLabel = isAuthenticated ? 'My Learning' : 'Get Started';
  const accountHref = isAuthenticated ? '/browse' : '/login';

  /* ─── EDITORIAL ────────────────────────────────────────────── */
  if (theme === 'editorial') {
    return (
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[var(--ed-paper)]/90 backdrop-blur-md border-b border-[var(--ed-rule)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-[22px] tracking-[-0.02em] text-[var(--ed-ink)]">
            {tenantName.split(' ').map((w, i) =>
              i === tenantName.split(' ').length - 1 && tenantName.split(' ').length > 1 ? (
                <em key={i}> {w}</em>
              ) : (
                <span key={i}>{i > 0 ? ' ' : ''}{w}</span>
              ),
            )}
          </Link>

          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-[var(--ed-ink)]">
            <Link
              to="/courses"
              className={`relative pb-1 ${isActive('/courses') ? 'text-[#2d4a3e]' : 'hover:text-[#2d4a3e]'}`}
            >
              Catalogue
            </Link>
            <a href="#instructor" className="hover:text-[#2d4a3e]">Faculty</a>
            <a href="#approach" className="hover:text-[#2d4a3e]">Approach</a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to={accountHref}
              className="font-mono text-[11px] uppercase tracking-[0.1em] px-4 py-2 border border-[var(--ed-ink)] rounded-full hover:bg-[var(--ed-ink)] hover:text-[var(--ed-paper)] transition-colors"
            >
              {isAuthenticated ? 'My Learning' : 'Sign in'}
            </Link>
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[var(--ed-rule)] bg-[var(--ed-paper)] py-3 px-4 flex flex-col gap-2">
            <Link to="/courses" onClick={() => setMobileMenuOpen(false)} className="py-2 font-medium">Catalogue</Link>
            <Link to={accountHref} onClick={() => setMobileMenuOpen(false)} className="py-2 font-mono text-xs uppercase tracking-wider">
              {isAuthenticated ? 'My Learning' : 'Sign in'}
            </Link>
          </div>
        )}
      </header>
    );
  }

  /* ─── WARM ─────────────────────────────────────────────────── */
  if (theme === 'warm') {
    return (
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[var(--warm-paper)]/95 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--warm-accent)] text-white -rotate-[6deg] shadow-[0_4px_12px_rgba(217,119,87,0.35)]"
              style={{ fontFamily: 'Caveat, cursive', fontSize: '22px', fontWeight: 600 }}
            >
              {initial.toLowerCase()}
            </span>
            <span className="font-warm-serif text-[22px] font-medium text-[var(--warm-ink)]">
              {tenantName}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 text-[15px] font-semibold text-[var(--warm-ink)]">
            <Link to="/courses" className={`px-3.5 py-2 rounded-full transition-colors ${isActive('/courses') ? 'bg-[var(--warm-paper-deep)]' : 'hover:bg-[var(--warm-paper-deep)]'}`}>
              Programmes
            </Link>
            <a href="#community" className="px-3.5 py-2 rounded-full hover:bg-[var(--warm-paper-deep)]">Community</a>
            <a href="#stories" className="px-3.5 py-2 rounded-full hover:bg-[var(--warm-paper-deep)]">Stories</a>
          </nav>

          <Link
            to={accountHref}
            className="hidden md:inline-flex items-center gap-2 bg-[var(--warm-ink)] text-[var(--warm-paper)] px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[var(--warm-accent-deep)] transition-all hover:-translate-y-0.5"
          >
            {isAuthenticated ? 'My Learning' : 'Sign in'}
          </Link>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[var(--warm-rule)] bg-[var(--warm-paper)] py-3 px-4 flex flex-col gap-2">
            <Link to="/courses" onClick={() => setMobileMenuOpen(false)} className="py-2 font-semibold">Programmes</Link>
            <Link to={accountHref} onClick={() => setMobileMenuOpen(false)} className="py-2 font-bold text-[var(--warm-accent-deep)]">
              {isAuthenticated ? 'My Learning' : 'Sign in'}
            </Link>
          </div>
        )}
      </header>
    );
  }

  /* ─── UTILITY ──────────────────────────────────────────────── */
  return (
    <header className="sticky top-0 z-40 bg-[var(--util-paper)] border-b border-[var(--util-rule)]">
      <div className="max-w-[1320px] mx-auto px-6 h-16 flex items-center justify-between gap-8">
        <Link to="/" className="flex items-center gap-2.5 font-semibold text-[16px] tracking-[-0.01em] text-[var(--util-ink)]">
          <span className="relative inline-flex items-center justify-center w-7 h-7 bg-[var(--util-ink)] text-[var(--util-paper)] font-util-mono font-semibold text-sm rounded">
            {initial}
            <span className="absolute -right-0.5 -bottom-0.5 w-1.5 h-1.5 bg-[var(--util-accent)] rounded-full" />
          </span>
          {tenantName}
          <span className="font-util-mono text-[11px] text-[var(--util-ink-faint)] hidden md:inline ml-2">
            / school
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-[14px] font-medium text-[var(--util-ink)]">
          <Link to="/courses" className={`px-3 py-1.5 rounded transition-colors hover:bg-[var(--util-mute)] inline-flex items-center gap-1.5 ${isActive('/courses') ? 'bg-[var(--util-mute)]' : ''}`}>
            Catalogue
          </Link>
          <a href="#curriculum" className="px-3 py-1.5 rounded hover:bg-[var(--util-mute)]">Curriculum</a>
          <a href="#faculty" className="px-3 py-1.5 rounded hover:bg-[var(--util-mute)]">Faculty</a>
          <a href="#pricing" className="px-3 py-1.5 rounded hover:bg-[var(--util-mute)]">Pricing</a>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/login"
            className="text-[14px] font-medium px-3.5 py-2 rounded hover:bg-[var(--util-mute)] transition-colors"
          >
            Sign in
          </Link>
          <Link
            to={browseHref}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--util-ink)] text-white text-[14px] font-semibold rounded hover:bg-[var(--util-accent)] transition-colors"
          >
            Apply
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[var(--util-rule)] py-3 px-6 flex flex-col gap-2">
          <Link to="/courses" onClick={() => setMobileMenuOpen(false)} className="py-2 font-medium">Catalogue</Link>
          <Link to={browseHref} onClick={() => setMobileMenuOpen(false)} className="py-2 font-semibold text-[var(--util-accent-ink)]">
            {browseLabel}
          </Link>
        </div>
      )}
    </header>
  );
};
